import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";

import QuestionChip from "components/Styled/QuestionChip";
import { fetcherAuth } from "utils/api";
import { normalizePhone } from "utils/form";
import { makeDeepCopy } from "utils/updateLocalState";

export default function UserNotificationsSettings() {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const {
    currentOrg,
    setUserNotifications,
    userNotifications,
    profile,
    setProfile,
  } = useContext(OrganizationUserContext);

  const handleChangePhone = (e: CustomEvent) => {
    const { value } = e.target;
    const copy = makeDeepCopy(profile);
    copy.phoneNumber = value;
    setProfile(copy);
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      let data = {
        ...userNotifications,
        ...profile,
      };

      const response = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "PUT",
        {},
        data,
      );
      setProfile(response.organizationProfile);
      setAlert("Profile saved", "success");
    } catch (error) {
      setAlert(
        error.message || "An error occured when updating profile",
        "warning",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (selectedOption: string) => {
    setUserNotifications((prev) => {
      if (!prev) {
        prev = {};
      }
      const copy = makeDeepCopy(prev);
      copy[selectedOption] = !copy[selectedOption];
      return copy;
    });
  };

  return (
    <>
      <Box>
        <Typography gutterBottom>
          How would you like to receive notifications and updates about your
          account?
        </Typography>

        <Typography variant="caption" color="textSecondary" paragraph>
          Select all that apply.
        </Typography>

        <Grid container gap={1}>
          <QuestionChip
            label="Email"
            isSelected={!!userNotifications?.optInEmail}
            onClick={() => handleSelect("optInEmail")}
          />
          <QuestionChip
            label="Phone"
            isSelected={!!userNotifications?.optInPhone}
            onClick={() => handleSelect("optInPhone")}
          />
          {userNotifications?.optInPhone && (
            <TextField
              value={normalizePhone(profile?.phoneNumber || "")}
              onChange={handleChangePhone}
              name="phoneNumber"
              required
              variant="standard"
              placeholder="Phone number"
            />
          )}
        </Grid>
      </Box>

      <LoadingButton
        loading={loading}
        sx={{ mt: 2 }}
        disableElevation
        onClick={handleSave}
      >
        Save
      </LoadingButton>
    </>
  );
}
